/**
 * Parsing and formatting for the money strings WooCommerce hands back.
 *
 * Lives here rather than beside any one feature because three of them now need it: the
 * LED landing page, the checkout, and the product structured data. Getting it wrong is
 * expensive in the last of those — Google disapproves a listing whose advertised price
 * disagrees with the page.
 */

/**
 * WooCommerce money arrives as a formatted entity string like `342,00&nbsp;€`, so
 * neither Number() nor a bare parseFloat() is safe: parseFloat stops at the comma and
 * silently drops the cents. Same regex as the checkout and CheckoutForm use.
 */
export const parsePrice = (price?: string | null): number =>
  parseFloat((price ?? '').replace(/&nbsp;|€|\s/g, '').replace(',', '.')) || 0;

/**
 * A price formatted the way schema.org wants it: a plain decimal string, dot separator,
 * two decimals — `"31.50"`, never `31.5` and never `"31,50 €"`.
 *
 * Null rather than "0.00" for anything that does not parse to a positive number, so the
 * caller drops the offer entirely instead of advertising a free product.
 */
export const formatSchemaPrice = (price?: string | null): string | null => {
  const amount = parsePrice(price);

  return amount > 0 ? amount.toFixed(2) : null;
};

/**
 * Whether a sale window is still open.
 *
 * Prices are baked into the page at build time, but WooCommerce reverts to the regular
 * price the moment the window closes. Checking the deadline at render time means a
 * stale deploy stops advertising a discount the checkout would no longer honour.
 *
 * No end date means an open-ended sale, which is the common case in this catalogue — so
 * that reads as live. An unparseable date is a data error and reads as *not* live: the
 * page then quotes the regular price, which is the safe direction to be wrong in.
 */
export const isSaleLive = (
  dateOnSaleTo?: string | null,
  now: Date = new Date()
): boolean => {
  if (!dateOnSaleTo) return true;

  const ends = new Date(dateOnSaleTo);

  if (Number.isNaN(ends.getTime())) return false;

  return now.getTime() < ends.getTime();
};
